import React, { useState, useEffect, useRef } from 'react';
import { X, Coins, ExternalLink, Check, Users, ChevronDown, Star, Sparkles } from 'lucide-react';
import { api, useGame } from '../context/GameContext';

/**
 * In-app preview for Discover task. Shows the artwork without navigating
 * away from the Mini App, so Direct-Link users don't lose the bot from
 * their chat list. User taps "Claim" → we call /visit + /claim internally.
 */
const DiscoverPreviewModal = ({ card, reward, language, onClaimed, onClose, tutorialMode = false }) => {
  const { haptic } = useGame();
  const [claiming, setClaiming] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [error, setError] = useState(null);
  const [owners, setOwners] = useState(null);
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [socialBump, setSocialBump] = useState(null);
  const [openingBanner, setOpeningBanner] = useState(true);
  // Quiz state — `selectedIndex` is the player's tap on one of three
  // options. `quizResult` carries the server's verdict and correct
  // index so we can highlight green/red AFTER the claim. We never
  // accept the server's correct_index before the player has
  // committed to an answer (that would let them see it via devtools
  // sniffing the network at the right moment).
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const hasQuiz = !!card?.question?.options?.length;
  const finalMultiplier = quizResult?.multiplier || 1;
  const finalReward = quizResult?.coins_earned ?? ((reward ?? 50) * finalMultiplier);

  // Stable ref to the parent's onClose so the quiz-reveal effect below
  // doesn't get torn down on every parent re-render (which used to
  // cancel the auto-close timers and leave the modal open forever).
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Quiz reveal hold: once the server returns the verdict, give the
  // player ~1.5s to actually SEE the green / red highlight before we
  // start the exit animation. This is the visible "did I get it
  // right?" beat — without it the modal can close before the network
  // round-trip lands.
  useEffect(() => {
    if (!hasQuiz || !quizResult) return;
    if (window.__discoverSafetyTimer) {
      clearTimeout(window.__discoverSafetyTimer);
      window.__discoverSafetyTimer = null;
    }
    // Verdict haptic — `notificationOccurred` gives a distinct
    // success/error pattern that's instantly recognisable as a result
    // (different from the light `impactOccurred` used for taps). Falls
    // back silently outside Telegram WebApp.
    try {
      const notif = window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred;
      if (notif) {
        notif.call(window.Telegram.WebApp.HapticFeedback,
          quizResult.answered_correctly ? 'success' : 'error');
      } else {
        haptic?.();
      }
    } catch (_) { /* noop */ }
    const revealHoldMs = 1500; // 1s green/red highlight + 500ms breathing
    const exitId = setTimeout(() => setExiting(true), revealHoldMs);
    const closeId = setTimeout(() => onCloseRef.current?.(), revealHoldMs + 350);
    return () => {
      clearTimeout(exitId);
      clearTimeout(closeId);
    };
  }, [hasQuiz, quizResult]);

  // "You discovered an artwork" banner — plays once on open, then hides
  // so it doesn't block the main info panel. Matches the reveal moment.
  useEffect(() => {
    if (!card) return;
    setOpeningBanner(true);
    const hideId = setTimeout(() => setOpeningBanner(false), 2600);
    return () => clearTimeout(hideId);
  }, [card]);

  // Fetch owners of this template once on mount.
  // Discover endpoint returns card_templates rows where `id` IS the
  // template_id; instance rows have `template_id` set explicitly.
  useEffect(() => {
    const tplId = card?.template_id || card?.id;
    if (!tplId) return;
    let cancelled = false;
    api
      .get(`/cards/template/${tplId}/owners`)
      .then((res) => {
        if (!cancelled) setOwners(res.data);
      })
      .catch(() => {
        if (!cancelled) setOwners({ count: 0, owners: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [card?.template_id, card?.id]);

  // Lock background page scroll while the modal is open. Without this
  // the Earn list scrolls behind the overlay when the user swipes
  // inside the modal — especially noticeable when the Owners list is
  // expanded and the modal itself becomes scrollable.
  useEffect(() => {
    if (!card) return;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyTouchAction: body.style.touchAction,
      bodyTop: body.style.top,
      bodyPosition: body.style.position,
      bodyWidth: body.style.width,
    };
    const scrollY = window.scrollY;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.touchAction = prev.bodyTouchAction;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [card]);

  const t = (key) => {
    const texts = {
      new_artwork: {
        en: 'Discover artwork',
        ru: 'Открой картину',
      },
      view_on_channel: {
        en: 'View Original',
        ru: 'Открыть оригинал',
      },
      claim_reward: {
        en: 'Claim reward',
        ru: 'Забрать награду',
      },
      quiz_year: {
        en: 'When was this painting created?',
        ru: 'Когда написана эта картина?',
      },
      quiz_artist: {
        en: 'Who is the artist?',
        ru: 'Кто автор картины?',
      },
      quiz_correct: {
        en: 'Correct! x5 bonus',
        ru: 'Верно! x5 бонус',
      },
      quiz_wrong: {
        en: 'Wrong answer · x1',
        ru: 'Неверный ответ · x1',
      },
      quiz_correct_was: {
        en: 'Correct answer:',
        ru: 'Правильный ответ:',
      },
      owners: {
        en: 'Owners',
        ru: 'Владельцы',
      },
      no_owners: {
        en: 'No one owns this yet',
        ru: 'Пока ни у кого нет',
      },
      xp_to_owners: {
        en: (n) => `+1 influence to ${n} player${n === 1 ? '' : 's'}`,
        ru: (n) => `+1 влияние для ${n} владельц${n === 1 ? 'а' : 'ев'}`,
      },
      you_discovered: {
        en: 'You discovered an artwork',
        ru: 'Вы открыли картину',
      },
    };
    return texts[key]?.[language] || texts[key]?.en || key;
  };

  const handleOpenChannel = () => {
    if (!card?.post_url) return;
    const tg = window.Telegram?.WebApp;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(card.post_url);
    } else {
      window.open(card.post_url, '_blank');
    }
  };

  const handleClose = () => {
    if (exiting) return;
    setExiting(true);
    // Match the CSS transition duration below (250ms)
    setTimeout(() => onClose?.(), 250);
  };

  const handleClaim = (forcedIndex = null) => {
    if (claiming || exiting) return;
    // Match the rest of the game: every committed tap fires a light
    // haptic so the player gets a physical confirmation that their
    // answer was registered, even before the green/red verdict lands.
    haptic?.();
    // Lock in the index we'll send to the server. For quiz cards this
    // is the option the player just tapped; for legacy non-quiz
    // cards it's null.
    const indexToSubmit = hasQuiz
      ? (forcedIndex !== null ? forcedIndex : selectedIndex)
      : null;
    if (hasQuiz && indexToSubmit === null) return;
    setSelectedIndex(indexToSubmit);
    setClaiming(true);
    setError(null);
    // (Social Influence XP for other owners was removed — we no longer
    // pre-set `socialBump` from `owners.count`. The animation that used
    // to show "+1 influence to N players" stays defined below but is
    // guarded by `socialBump > 0`, which is now never true unless the
    // server returns a non-zero `contributed_to_owners`.)
    //
    // For QUIZ cards: don't kick off the close timer here. We trigger
    // it from a useEffect that fires when `quizResult` is set so the
    // player gets to see the green/red verdict for ~1.5s regardless
    // of API latency. A safety fallback closes after 4s if the API
    // never responds.
    //
    // For LEGACY (non-quiz) cards: keep the original snappy flow —
    // start the close timer immediately on tap.
    if (!hasQuiz) {
      const holdMs = 1500;
      setTimeout(() => setExiting(true), holdMs);
      setTimeout(() => onClose?.(), holdMs + 350);
    } else {
      // Safety net: if /claim never resolves, close after 4s anyway
      const safetyId = setTimeout(() => {
        setExiting(true);
        setTimeout(() => onClose?.(), 350);
      }, 4000);
      // Stash on window so we can clear from the result effect — we
      // can't useRef here because this lives inside an event handler.
      window.__discoverSafetyTimer = safetyId;
    }
    (async () => {
      try {
        // `/visit` used to gate `/claim` server-side — we awaited it
        // sequentially. The claim endpoint now auto-marks the card as
        // visited, so we can skip the round-trip entirely. The verdict
        // (green/red) appears ~300-700ms sooner — closer to native
        // quiz-app feel.
        const res = await api.post(`/earn/me/discover/${card.id}/claim`, {
          selected_index: indexToSubmit,
        });
        setQuizResult({
          multiplier: res.data?.multiplier || 1,
          correct_index: res.data?.correct_index ?? null,
          answered_correctly: !!res.data?.answered_correctly,
          coins_earned: res.data?.coins_earned,
        });
        if (typeof res.data?.contributed_to_owners === 'number') {
          setSocialBump(res.data.contributed_to_owners);
        }
        onClaimed?.({ ...res.data, cardId: card.id });
      } catch (err) {
        onClaimed?.({
          error: err.response?.data?.detail || 'Error',
          cardId: card.id,
        });
      }
    })();
  };

  // Pinch-to-zoom on the artwork: two fingers scale the image around
  // the pinch midpoint (with pan following the fingers); releasing
  // snaps it back to the resting state. Implemented with direct style
  // mutation on a dedicated wrapper layer (no React re-renders) and
  // non-passive listeners so preventDefault() can block the browser's
  // native page zoom/scroll inside Telegram WebView.
  const zoomBoxRef = useRef(null);
  const zoomLayerRef = useRef(null);
  useEffect(() => {
    const box = zoomBoxRef.current;
    const layer = zoomLayerRef.current;
    if (!box || !layer) return;
    const st = { active: false, startDist: 0, startMid: null };
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mid = (t, rect) => ({
      x: (t[0].clientX + t[1].clientX) / 2 - rect.left,
      y: (t[0].clientY + t[1].clientY) / 2 - rect.top,
    });
    const onStart = (e) => {
      if (e.touches.length !== 2) return;
      e.preventDefault();
      const rect = box.getBoundingClientRect();
      st.active = true;
      st.startDist = dist(e.touches) || 1;
      st.startMid = mid(e.touches, rect);
      layer.style.transition = 'none';
      layer.style.transformOrigin = `${st.startMid.x}px ${st.startMid.y}px`;
    };
    const onMove = (e) => {
      if (!st.active || e.touches.length !== 2) return;
      e.preventDefault();
      const rect = box.getBoundingClientRect();
      const scale = Math.min(4, Math.max(1, dist(e.touches) / st.startDist));
      const m = mid(e.touches, rect);
      layer.style.transform = `translate(${m.x - st.startMid.x}px, ${m.y - st.startMid.y}px) scale(${scale})`;
    };
    const reset = () => {
      if (!st.active) return;
      st.active = false;
      layer.style.transition = 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)';
      layer.style.transform = 'translate(0px, 0px) scale(1)';
    };
    const onEnd = (e) => { if (e.touches.length < 2) reset(); };
    box.addEventListener('touchstart', onStart, { passive: false });
    box.addEventListener('touchmove', onMove, { passive: false });
    box.addEventListener('touchend', onEnd, { passive: true });
    box.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      box.removeEventListener('touchstart', onStart);
      box.removeEventListener('touchmove', onMove);
      box.removeEventListener('touchend', onEnd);
      box.removeEventListener('touchcancel', onEnd);
    };
  }, [card]);

  if (!card) return null;

  return (
    <div
      data-testid="discover-preview-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center pb-[8vh] sm:pb-0"
      style={{
        transition: 'opacity 250ms ease-out',
        opacity: exiting ? 0 : 1,
        pointerEvents: exiting ? 'none' : 'auto',
      }}
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-md bg-[#0a0a12] rounded-t-3xl sm:rounded-3xl overflow-hidden border border-[#D4AF37]/20"
        style={{
          transition: 'transform 250ms ease-out, opacity 250ms ease-out',
          transform: exiting
            ? 'translateY(24px) scale(0.96)'
            : claiming
              ? 'scale(1.01)'
              : 'scale(1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          data-testid="discover-preview-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Image */}
        <div
          ref={zoomBoxRef}
          data-testid="discover-preview-image-box"
          className="relative aspect-square bg-black overflow-hidden"
          style={{ touchAction: 'none' }}
        >
          <div
            ref={zoomLayerRef}
            data-testid="discover-preview-zoom-layer"
            className="w-full h-full"
            style={{ willChange: 'transform' }}
          >
            <img
              src={card.image_url}
              alt={card.title}
              className="w-full h-full object-cover"
              draggable={false}
              style={{
                objectPosition: `${card.image_focus_x ?? 50}% ${card.image_focus_y ?? 50}%`,
                transition: 'transform 500ms ease-out, filter 500ms ease-out',
                transform: claiming ? 'scale(1.05)' : 'scale(1)',
                filter: claiming ? 'brightness(1.15)' : 'brightness(1)',
              }}
            />
          </div>

          {/* Opening banner — "You discovered an artwork" */}
          {openingBanner && !claiming && (
            <>
              {/* Dim gradient behind the title for readability */}
              <div
                className="absolute inset-x-0 top-0 h-24 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 100%)',
                  animation: 'dpm-fade-in 400ms ease-out forwards',
                }}
              />
              <div
                data-testid="discover-preview-opening-banner"
                className="absolute top-4 left-1/2 pointer-events-none"
                style={{
                  transform: 'translateX(-50%)',
                  animation:
                    'dpm-banner-in 600ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards, dpm-banner-out 500ms ease-in 2100ms forwards',
                }}
              >
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#D4AF37]/15 backdrop-blur-md border border-[#D4AF37]/40 shadow-[0_4px_20px_rgba(212,175,55,0.3)]">
                  <Sparkles
                    className="w-3.5 h-3.5 text-[#D4AF37]"
                    style={{ animation: 'dpm-sparkle 1800ms ease-in-out infinite' }}
                  />
                  <span className="text-[11px] font-medium tracking-wide text-[#FDE68A] uppercase">
                    {t('you_discovered')}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Claim celebration overlay */}
          {claiming && (
            <div
              data-testid="discover-preview-success-overlay"
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle at center, rgba(212,175,55,0.32) 0%, rgba(212,175,55,0) 65%)',
                animation: 'dpm-fade-in 280ms ease-out forwards',
              }}
            >
              {/* Checkmark burst — uses the game's signature gold so
                  the success moment is visually consistent with the
                  rest of the app (coin pill, accents, CTA highlights).
                  Green was reading as a generic "OK" toast rather
                  than a celebratory reward. */}
              <div
                className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.85)]"
                style={{
                  animation:
                    'dpm-burst 550ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                }}
              >
                <Check className="w-10 h-10 text-[#0a0a12]" strokeWidth={3} />
              </div>

              {/* Floating reward pill (flies up from center-top) */}
              <div
                className="absolute top-[30%] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#D4AF37] text-[#0a0a12] font-mono font-bold text-sm shadow-[0_4px_20px_rgba(212,175,55,0.6)]"
                style={{
                  animation:
                    'dpm-coin-fly 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards',
                }}
              >
                <Coins className="w-4 h-4" />+{finalReward}
              </div>

              {/* Social influence: "+1 XP to N players" (flies up from bottom) */}
              {socialBump > 0 && (
                <div
                  data-testid="discover-preview-social-bump"
                  className="absolute bottom-[18%] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#3B82F6] text-white font-medium text-[11px] shadow-[0_4px_20px_rgba(59,130,246,0.5)]"
                  style={{
                    animation:
                      'dpm-influence-fly 1400ms cubic-bezier(0.22, 1, 0.36, 1) 200ms forwards',
                  }}
                >
                  <Users className="w-3 h-3" />
                  {language === 'ru'
                    ? `+1 влияние · ${socialBump} игрок${socialBump === 1 ? '' : socialBump < 5 ? 'а' : 'ов'}`
                    : `+1 influence · ${socialBump} player${socialBump === 1 ? '' : 's'}`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Local keyframes (scoped via unique prefix dpm-) */}
        <style>{`
          @keyframes dpm-fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes dpm-burst {
            0% { transform: scale(0.2); opacity: 0; }
            40% { transform: scale(1.15); opacity: 1; }
            70% { transform: scale(0.95); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes dpm-coin-fly {
            0% { transform: translateY(0) scale(0.4); opacity: 0; }
            25% { transform: translateY(-10px) scale(1); opacity: 1; }
            100% { transform: translateY(-90px) scale(0.8); opacity: 0; }
          }
          @keyframes dpm-influence-fly {
            0%   { transform: translate(0, 10px) scale(0.4); opacity: 0; }
            30%  { transform: translate(0, -4px) scale(1); opacity: 1; }
            65%  { transform: translate(0, -28px) scale(1); opacity: 1; }
            100% { transform: translate(0, -70px) scale(0.9); opacity: 0; }
          }
          @keyframes dpm-banner-in {
            0%   { transform: translateX(-50%) translateY(-30px) scale(0.85); opacity: 0; }
            60%  { transform: translateX(-50%) translateY(4px) scale(1.05); opacity: 1; }
            100% { transform: translateX(-50%) translateY(0) scale(1); opacity: 1; }
          }
          @keyframes dpm-banner-out {
            0%   { transform: translateX(-50%) translateY(0); opacity: 1; }
            100% { transform: translateX(-50%) translateY(-12px); opacity: 0; }
          }
          @keyframes dpm-sparkle {
            0%, 100% { transform: rotate(0deg) scale(1); opacity: 1; }
            50%      { transform: rotate(25deg) scale(1.2); opacity: 0.7; }
          }
          /* Quiz option buttons breathe a soft gold pulse before any
             answer is given — makes it visually obvious these chips
             are the actual answer slot, not just labels. */
          @keyframes dpm-quiz-pulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.0);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 0 14px 1px rgba(212, 175, 55, 0.45);
              transform: scale(1.025);
            }
          }
          .dpm-quiz-pulse {
            animation: dpm-quiz-pulse 1.6s ease-in-out infinite;
          }
          /* While the /claim round-trip is in flight, the selected
             option pulses fast (golden) so the player feels their tap
             was registered even on a 500-1000ms mobile network hop. */
          @keyframes dpm-quiz-thinking {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.45);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 0 16px 2px rgba(212, 175, 55, 0.65);
              transform: scale(1.04);
            }
          }
          .dpm-quiz-thinking {
            animation: dpm-quiz-thinking 0.65s ease-in-out infinite;
          }
          /* Tap-hint chevrons subtly bob to draw the eye downward. */
          @keyframes dpm-tap-bob {
            0%, 100% { transform: translateY(0); opacity: 0.7; }
            50%      { transform: translateY(2px); opacity: 1; }
          }
          .dpm-tap-hint { animation: dpm-tap-bob 1.4s ease-in-out infinite; }
        `}</style>

        {/* Details */}
        <div className="p-4">
          <h2 className="text-lg font-heading text-[#F5F5F7] leading-tight">
            {card.title}
          </h2>
          {/* Artist/year reveal: hidden while the quiz is unanswered so
              the player has to commit to an option without reading the
              answer right above the buttons. Shown after the verdict
              comes back, doubling as the "learn the truth" moment.
              Skips rendering entirely when artist metadata is missing —
              "Unknown artist" placeholder felt like a bug to players. */}
          {(!hasQuiz || quizResult) && card.artist && (
            <p className="text-sm text-[#A1A1AA] mt-0.5">
              {card.artist}{card.year ? ` · ${card.year}` : ''}
            </p>
          )}

          {/* Quiz moved to the bottom of the modal so the option
              buttons literally sit where the old Claim CTA was.
              Tapping an option fires `handleClaim(i)` immediately —
              no separate Claim button to press. */}

          {error && (
            <p className="mt-3 text-xs text-[#EF4444]">{error}</p>
          )}

          {/* Owners accordion — always rendered to reserve layout space
              and prevent the modal from jumping when the API responds */}
          <div className="mt-3 border-t border-white/5 pt-3">
            <button
              onClick={() => owners?.count > 0 && setOwnersOpen((v) => !v)}
              disabled={!owners || owners.count === 0}
              data-testid="discover-preview-owners-toggle"
              className="w-full flex items-center justify-between text-left disabled:cursor-default"
            >
              <span className="inline-flex items-center gap-1.5 text-xs text-[#A1A1AA]">
                <Users className="w-3.5 h-3.5 text-[#D4AF37]" />
                {t('owners')}
                {owners === null ? (
                  <span className="inline-block w-6 h-3 rounded bg-white/5 animate-pulse" />
                ) : (
                  <span className="text-[#D4AF37] font-mono">({owners.count})</span>
                )}
              </span>
              {owners && owners.count > 0 && (
                <ChevronDown
                  className={`w-3.5 h-3.5 text-[#52525B] transition-transform ${
                    ownersOpen ? 'rotate-180' : ''
                  }`}
                />
              )}
            </button>

            {ownersOpen && owners && owners.count > 0 && (
              <div
                data-testid="discover-preview-owners-list"
                className="mt-2 space-y-1.5 max-h-44 overflow-y-auto pr-1"
              >
                {owners.owners.map((o) => (
                  <div
                    key={o.user_id}
                    className="flex items-center gap-2 py-1"
                  >
                    {o.photo_url ? (
                      <img
                        src={o.photo_url}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover bg-[#1a1a2e] flex-shrink-0"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#1a1a2e] flex items-center justify-center text-[10px] text-[#A1A1AA] flex-shrink-0">
                        {(o.username || o.first_name || '?')
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                    )}
                    <span className="text-xs text-[#F5F5F7] truncate flex-1">
                      {o.username
                        ? `@${o.username}`
                        : o.first_name || 'Player'}
                    </span>
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-[#D4AF37] font-mono">
                      <Star className="w-2.5 h-2.5" fill="currentColor" />
                      {o.rating}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className="flex flex-col gap-2 mt-4 items-stretch"
            style={{
              transition: 'opacity 200ms ease-out',
              opacity: claiming && !hasQuiz ? 0.4 : 1,
            }}
          >
            {card.post_url && !claiming && !quizResult && (
              <button
                onClick={handleOpenChannel}
                data-testid="discover-preview-open-channel"
                className="w-full py-2.5 rounded-xl border border-[#3B82F6]/40 text-[#3B82F6] text-xs font-medium flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {t('view_on_channel')}
              </button>
            )}

            {/* Quiz CTA: option buttons sit where the Claim button
                used to be. Tapping an option commits the claim
                immediately — no separate confirm step. After the
                server responds the verdict overlay (above) holds the
                modal open for a moment so the player can read it. */}
            {hasQuiz ? (
              <div data-testid="discover-quiz">
                {/* Verdict header — bold, large, color-coded so the
                    user can't miss whether they were right. Reuses
                    the same slot as the prompt before the answer. */}
                {!quizResult ? (
                  <>
                    <p className="text-xs text-[#A1A1AA] mb-1 text-center">
                      {card.question.type === 'year' ? t('quiz_year') : t('quiz_artist')}
                      <span className="ml-1 text-[#D4AF37] font-semibold">· x5</span>
                    </p>
                    {/* Tiny tap hint so first-time players don't miss
                        that the options below ARE the answer buttons.
                        Animated chevron points down at the row. */}
                    <p className="text-[10px] text-[#D4AF37]/70 mb-2 text-center flex items-center justify-center gap-1 dpm-tap-hint">
                      <ChevronDown className="w-3 h-3" />
                      {language === 'ru' ? 'Нажмите вариант ответа' : 'Tap an answer'}
                      <ChevronDown className="w-3 h-3" />
                    </p>
                  </>
                ) : quizResult.answered_correctly ? (
                  <div
                    data-testid="discover-quiz-verdict"
                    className="mb-2 text-center"
                  >
                    <p className="text-sm font-bold text-[#10B981] flex items-center justify-center gap-1.5">
                      <span>{t('quiz_correct')}</span>
                      <span className="inline-flex items-center gap-0.5 text-[13px] font-mono bg-[#10B981]/15 border border-[#10B981]/30 rounded-full px-1.5 py-0.5">
                        <Coins className="w-3 h-3" />+{finalReward}
                      </span>
                    </p>
                  </div>
                ) : (
                  <div
                    data-testid="discover-quiz-verdict"
                    className="mb-2 text-center"
                  >
                    <p className="text-sm font-bold text-[#EF4444] flex items-center justify-center gap-1.5">
                      <span>{t('quiz_wrong')}</span>
                      <span className="inline-flex items-center gap-0.5 text-[13px] font-mono text-[#FDE68A] bg-[#FDE68A]/10 border border-[#FDE68A]/25 rounded-full px-1.5 py-0.5">
                        <Coins className="w-3 h-3" />+{finalReward}
                      </span>
                    </p>
                    <p className="text-[11px] text-[#A1A1AA] mt-0.5">
                      {t('quiz_correct_was')}{' '}
                      <span className="text-[#10B981] font-medium">
                        {card.question.options[quizResult.correct_index]}
                      </span>
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-1.5">
                  {card.question.options.map((opt, i) => {
                    const isSelected = selectedIndex === i;
                    const isCorrectAfter = quizResult && quizResult.correct_index === i;
                    const isWrongPickedAfter = quizResult && isSelected && !quizResult.answered_correctly;
                    // Tutorial onboarding: the very first discover
                    // card includes `correct_index` straight from the
                    // server (only during the tutorial step — see
                    // backend `get_user_earn`). When set, we highlight
                    // the correct option with a golden arrow + softer
                    // glow on the others, and disable every option
                    // EXCEPT the correct one so the player can't
                    // accidentally fail the first quiz.
                    const tutorialHint = tutorialMode && card.question?.tutorial_hint === true;
                    const tutorialCorrectIdx = tutorialHint ? card.question?.correct_index : null;
                    const isTutorialPointed = tutorialHint && tutorialCorrectIdx === i;
                    const isTutorialBlocked = tutorialHint && tutorialCorrectIdx !== null && tutorialCorrectIdx !== i;
                    const styles = quizResult
                      ? isCorrectAfter
                        ? 'border-[#10B981] bg-[#10B981]/20 text-[#10B981] ring-2 ring-[#10B981]/40'
                        : isWrongPickedAfter
                          ? 'border-[#EF4444] bg-[#EF4444]/15 text-[#FCA5A5] ring-2 ring-[#EF4444]/40'
                          : 'border-white/5 bg-white/5 text-[#52525B] opacity-60'
                      : isSelected
                        ? `border-[#D4AF37] bg-[#D4AF37]/15 text-[#FDE68A]${claiming && !quizResult ? ' dpm-quiz-thinking' : ''}`
                        : isTutorialPointed
                          // Stronger highlight for the tutorial-correct
                          // option: brighter golden ring, thicker
                          // border, slight scale-up, brighter text.
                          // Players were missing the previous, more
                          // subtle treatment.
                          ? 'border-2 border-[#FFD700] bg-[#D4AF37]/30 text-[#FEF3C7] ring-4 ring-[#FFD700]/50 scale-[1.05] shadow-[0_0_18px_rgba(255,215,0,0.55)] dpm-quiz-pulse'
                          : isTutorialBlocked
                            ? 'border-white/5 bg-white/[0.03] text-[#52525B] opacity-40 cursor-not-allowed'
                            // Before any answer: bold golden border + soft
                            // pulsing glow so the buttons read as the
                            // primary CTA, not just hint chips. Stagger the
                            // pulse delay (per-option) so all three breathe
                            // out of sync — looks alive, not glitched.
                            : 'border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#FDE68A] hover:bg-[#D4AF37]/20 hover:border-[#D4AF37] active:scale-[0.97] dpm-quiz-pulse';
                    const styleAttr = !quizResult && !isSelected && !tutorialHint
                      ? { animationDelay: `${i * 220}ms` }
                      : undefined;
                    // Artist questions show only the surname; year
                    // questions display as-is.
                    const display = card.question.type === 'artist'
                      ? String(opt).trim().split(/\s+/).pop()
                      : opt;
                    return (
                      <div key={i} className="relative">
                        {/* Tutorial arrow — bigger, brighter, thicker
                            stroke so it matches the visual weight of
                            the other tutorial arrows in the app
                            (Earn discover button, Upgrades panel).
                            Lifted on its own stacking layer so the
                            scale-up on the option doesn't clip it. */}
                        {isTutorialPointed && (
                          <div
                            data-testid="tutorial-arrow-quiz"
                            className="absolute -top-9 left-1/2 -translate-x-1/2 pointer-events-none animate-tut-arrow z-30"
                          >
                            <ChevronDown
                              className="w-10 h-10 text-[#FFD700] drop-shadow-[0_0_10px_rgba(255,215,0,1)]"
                              strokeWidth={4}
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          disabled={!!quizResult || claiming || isTutorialBlocked}
                          onClick={() => handleClaim(i)}
                          data-testid={`discover-quiz-option-${i}`}
                          title={String(opt)}
                          style={styleAttr}
                          className={`w-full px-2 py-3 rounded-xl border text-[12px] font-semibold transition-all text-center truncate ${styles}`}
                        >
                          {display}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Legacy non-quiz fallback: keep the Claim button. */
              <button
                onClick={() => handleClaim()}
                disabled={claiming}
                data-testid="discover-preview-claim"
                className="w-full py-4 rounded-xl bg-[#10B981] text-white text-sm font-semibold flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
                {t('claim_reward')}
                <span className="inline-flex items-center gap-0.5 bg-white/20 rounded-full px-1.5 py-0.5">
                  <Coins className="w-3 h-3" />
                  +{finalReward}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoverPreviewModal;
